import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Typography, Stack, Grid, IconButton, Tooltip } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import InfoField from "./infoField";
import { incomingCallPopperSx } from "./overlayPolicy";

import type { Voyage } from "../../../utils/types/call";

// Types
interface VoyagesListProps {
  voyages: Voyage[] | null | undefined;
}

const VoyageHeader = ({
  vesselName,
  voyage,
  isExpanded,
  onClick,
}: {
  vesselName: string;
  voyage: string;
  isExpanded: boolean;
  onClick: () => void;
}) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    onClick={onClick}
    sx={{
      cursor: "pointer",
      bgcolor: "action.hover",
      height: 35,
      px: 1,
      py: 1,
      borderRadius: 1,
    }}
  >
    <Typography
      variant="subtitle2"
      overflow="hidden"
      textOverflow="ellipsis"
      whiteSpace="nowrap"
    >
      {vesselName} - {voyage}
    </Typography>
    <IconButton size="small">
      {isExpanded ? (
        <KeyboardArrowUp fontSize="small" />
      ) : (
        <KeyboardArrowDown fontSize="small" />
      )}
    </IconButton>
  </Stack>
);

const VoyageDetails = ({ voyage }: { voyage: Voyage }) => (
  <Grid container spacing={1.5}>
    <Grid container item spacing={1.5}>
      <Grid item xs={6}>
        <InfoField label="Management" value={voyage.management} />
      </Grid>
      <Grid item xs={6}>
        <InfoField label="Status" value={voyage.status} />
      </Grid>
    </Grid>
    <Grid container item spacing={1.5}>
      <Grid item xs={6}>
        <InfoField
          label="Operator"
          valueJsx={
            <Tooltip
              arrow
              slotProps={{ popper: { sx: incomingCallPopperSx } }}
              title={
                voyage.operatorContactName ? (
                  <Box sx={{ p: 0.25 }}>
                    <Typography display="block" fontWeight={700} variant="caption" >
                      {voyage.operatorContactName}
                    </Typography>
                    {voyage.operatorContactPhone && (
                      <Typography display="block" sx={{ color: "grey.300" }} variant="caption">
                        {voyage.operatorContactPhone.replace(/\s*ext\..*$/i, "")}
                      </Typography>
                    )}
                    {voyage.operatorContactEmail && (
                      <Typography display="block" sx={{ color: "grey.300" }} variant="caption">
                        {voyage.operatorContactEmail}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  ""
                )
              }
            >
              <Typography
                variant="subtitle2"
                fontWeight={500}
                sx={{ overflowWrap: "break-word", whiteSpace: "normal" }}
              >
                {voyage.operatorContactName
                  ? `${(voyage.operator || "Unknown").trim()}, ${voyage.operatorContactName}`
                  : (voyage.operator || "Unknown").trim()}
              </Typography>
            </Tooltip>
          }
        />
      </Grid>
      <Grid item xs={6}>
        <InfoField label="Agent" value={voyage.agent} />
      </Grid>
      {voyage.secondaryAgent && (
        <Grid item xs={6}>
          <InfoField label="Backup" value={voyage.secondaryAgent} />
        </Grid>
      )}
    </Grid>
    <Grid container item spacing={1.5}>
      <Grid item xs={6}>
        <InfoField label="Current Berth" value={voyage.currentBerth} />
      </Grid>
      <Grid item xs={6}>
        <InfoField label="Next Berth" value={voyage.nextBerth} />
      </Grid>
    </Grid>
  </Grid>
);
const CargoItem = ({ cargo, isLast : _isLast }: { cargo: any; isLast: boolean }) => {
  const navigate = useNavigate();

  const handleEditCargo = () => {
    // Navigate to the cargo edit screen using the berthId
    navigate(`/voyages/${cargo.jobId}/berthrotations/${cargo.berthId}/cargoes`);
  };

  return (
    <Box
      mb={1}
      p={1}
      borderRadius={1}
      sx={{
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography
          variant="subtitle2"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {cargo.grade || "Unknown"} - {cargo.status || "Unknown"}
        </Typography>
        <IconButton size="small" onClick={handleEditCargo} title="Edit cargo">
          <EditIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Box p={1}>
        <Grid container spacing={1}>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <InfoField label="Charterer" value={cargo.charterer} />
            </Grid>
            <Grid item xs={6}>
              <InfoField label="Quantity" value={cargo.quantity} />
            </Grid>
            <Grid item xs={6}>
              <InfoField label="Option" value={cargo.options} />
            </Grid>
            <Grid item xs={6}>
              <InfoField label="Surveyor" value={cargo.surveyor} />
            </Grid>
            <Grid item xs={6}>
              <InfoField label="Parcel" value={cargo.parcel} />
            </Grid>
            <Grid item xs={6}>
              <InfoField label="Berth" value={cargo.berth} />
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

// Main Component
const VoyagesList = ({ voyages }: VoyagesListProps) => {
  const [expandedVoyages, setExpandedVoyages] = useState<
    Record<number, boolean>
  >({});

  const toggleVoyage = (index: number) => {
    setExpandedVoyages((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <Box width="100%">
      {voyages?.map((voyage, index) => (
        <Box key={index} mb={0.5} width="100%">
          <VoyageHeader
            vesselName={voyage.vesselName}
            voyage={voyage.voyage}
            isExpanded={expandedVoyages[index]}
            onClick={() => toggleVoyage(index)}
          />

          <Box mt={1} px={1}>
            <VoyageDetails voyage={voyage} />
            {expandedVoyages[index] && (
              <Box mt={2}>
                <Typography variant="subtitle2" my={2}>
                  Cargo Details
                </Typography>
                {voyage.cargo.length > 0 ? (
                  voyage.cargo.map((cargo, idx) => (
                    <CargoItem
                      key={idx}
                      cargo={cargo}
                      isLast={idx === voyage.cargo.length - 1}
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No cargo details available.
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default VoyagesList;
